import React from "react";
import { useOutletContext } from "react-router-dom";
import { uniforms_getOrders, uniforms_createOrder } from "../../lib/firestoreAdapters";
import { USE_LOCAL_FALLBACK } from "../../config/firebaseConfig";

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (!lines.length) return [];
  const headers = lines[0].split(",").map(h => h.trim().toLowerCase());
  return lines.slice(1).map(line => {
    const cols = line.split(",").map(c => c.trim());
    const obj = {};
    headers.forEach((h, i) => obj[h] = cols[i] || "");
    return obj;
  });
}

export default function UniformsOrders() {
  const { campus } = useOutletContext();
  const [list, setList] = React.useState([]);
  const [showNew, setShowNew] = React.useState(false);
  const [form, setForm] = React.useState({ supplier: "", notes: "", itemsCSV: "" });
  const [saving, setSaving] = React.useState(false);
  const [info, setInfo] = React.useState("");

  React.useEffect(() => {
    (async () => {
      try {
        const rows = await uniforms_getOrders(campus);
        setList(rows);
      } catch {
        setList([]);
      }
    })();
  }, [campus]);

  async function save() {
    const rows = parseCSV(form.itemsCSV);
    const items = rows.map(r => ({
      product: r.product || r.name || "",
      size: r.size || "",
      qty: Number(r.qty || r.quantity || 0)
    })).filter(it => it.product && (it.size) && Number(it.qty || 0) > 0);
    if (!items.length) { setInfo("No items parsed. CSV headers: product,size,qty"); return; }
    setSaving(true); setInfo("");
    try {
      await uniforms_createOrder(campus, { supplier: form.supplier, notes: form.notes, items });
      const rows2 = await uniforms_getOrders(campus);
      setList(rows2);
      setShowNew(false);
      setForm({ supplier: "", notes: "", itemsCSV: "" });
    } catch (e) {
      setInfo("Network issue while saving. Please retry.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      {USE_LOCAL_FALLBACK && (
        <div className="alert alert-warning">Development mode: using local storage. In production this uses Firestore.</div>
      )}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Orders — {campus.toUpperCase()}</h5>
        <button className="btn btn-success" onClick={() => setShowNew(true)}>New Order</button>
      </div>
      {showNew && (
        <div className="card mb-3">
          <div className="card-header">New Order</div>
          <div className="card-body">
            <div className="row g-2 mb-2">
              <div className="col-md-4"><input className="form-control" placeholder="Supplier" value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} /></div>
              <div className="col-md-8"><input className="form-control" placeholder="Notes (optional)" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <div className="mb-2">
              <label className="form-label">Items CSV</label>
              <textarea className="form-control" rows={6} placeholder="product,size,qty" value={form.itemsCSV} onChange={e => setForm({ ...form, itemsCSV: e.target.value })} />
            </div>
            {info && <div className="text-danger mb-2">{info}</div>}
          </div>
          <div className="card-footer d-flex justify-content-end gap-2">
            <button className="btn btn-secondary" onClick={() => setShowNew(false)} disabled={saving}>Cancel</button>
            <button className="btn btn-success" onClick={save} disabled={saving}>Save Order</button>
          </div>
        </div>
      )}
      <div className="table-responsive">
        <table className="table table-bordered">
          <thead className="table-light">
            <tr>
              <th>Date</th><th>Supplier</th><th>Status</th><th>Items</th><th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan="5" className="text-center text-muted">No orders recorded.</td></tr>
            ) : list.map(r => (
              <tr key={r.id}>
                <td>{r.date}</td>
                <td>{r.supplier || "-"}</td>
                <td>{r.status || "open"}</td>
                <td>{Array.isArray(r.items) ? r.items.map((it, idx) => <div key={idx}>{it.product} {it.size} × {it.qty}</div>) : "-"}</td>
                <td>{r.notes || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
